import { useRef, useState } from "react";
import { useAppDispatch } from "@/app/hooks";
import { api } from "@/api/apiSlice";
import { createReportUpload } from "@/api/report/createReportUpload";
import { toReportUploadListTagId } from "@/api/report/getReportUploadList";
import { ReportTags } from "@/api/report/ReportTags";
import { uploadReportFile } from "@/api/report/uploadReportFile";
import { REPORT_UPLOAD_KINDS } from "@/api/report/_models/ReportUpload";
import type { ReportUploadKind } from "@/api/report/_models/ReportUpload";
import { toErrorText } from "./snapshot.utils";

export type SnapshotUploadFileStatus = "uploading" | "uploaded" | "failed";

/** A file dropped on a Snapshot page, as it uploads. */
export interface SnapshotUploadFile {
  id: string;
  /** Local state only: a File isn't serialisable, so it never goes in Redux. */
  file: File;
  status: SnapshotUploadFileStatus;
  /** 0..1 while uploading. */
  progress: number;
  error: string | null;
  /** Once uploaded: its S3 key. */
  key: string | null;
  uploadedAt: string | null;
}

export interface SnapshotUploadEntry {
  files: SnapshotUploadFile[];
}

export type SnapshotUploadState = Record<ReportUploadKind, SnapshotUploadEntry>;

const EMPTY_STATE: SnapshotUploadState = {
  toe: { files: [] },
  toll: { files: [] },
  estore: { files: [] },
  "toll-inventory": { files: [] },
  "estore-inventory": { files: [] },
};

const REPORTS: ReportUploadKind[] = REPORT_UPLOAD_KINDS;

/**
 * A Snapshot page's uploads, per report. A dropped file uploads straight away and stays here once
 * it's up (with its S3 key), so its row keeps its place; uploaded files are dropped on a new day.
 */
export const useSnapshotUpload = () => {
  const [state, setState] = useState<SnapshotUploadState>(EMPTY_STATE);
  const stateRef = useRef(state);
  stateRef.current = state;
  const [requestUpload] = createReportUpload.useMutation();
  const dispatch = useAppDispatch();
  const isUploading = REPORTS.some((report) => state[report].files.some((f) => f.status === "uploading"));

  const updateEntry = (report: ReportUploadKind, update: (entry: SnapshotUploadEntry) => SnapshotUploadEntry) => {
    setState((current) => {
      const entry = update(current[report]);
      return { ...current, [report]: entry };
    });
  };

  const updateFile = (report: ReportUploadKind, id: string, changes: Partial<SnapshotUploadFile>) => {
    updateEntry(report, (entry) => {
      const files = entry.files.map((f) => {
        if (f.id !== id) return f;
        return { ...f, ...changes };
      });
      return { ...entry, files };
    });
  };

  const removeFile = (report: ReportUploadKind, id: string) => {
    updateEntry(report, (entry) => {
      const files = entry.files.filter((f) => f.id !== id);
      return { ...entry, files };
    });
  };

  const uploadFile = async (report: ReportUploadKind, item: SnapshotUploadFile, date: string) => {
    updateFile(report, item.id, { status: "uploading", progress: 0, error: null, key: null, uploadedAt: null });
    try {
      const target = await requestUpload({ report, filename: item.file.name, date }).unwrap();
      const handleProgress = (fraction: number) => {
        updateFile(report, item.id, { progress: fraction });
      };
      await uploadReportFile(target, item.file, handleProgress);
      updateFile(report, item.id, { status: "uploaded", progress: 1, key: target.key, uploadedAt: new Date().toISOString() });
      const tagId = toReportUploadListTagId({ report, date });
      const invalidateUploadList = api.util.invalidateTags([{ type: ReportTags.ReportUpload, id: tagId }]);
      dispatch(invalidateUploadList);
    }
    catch (e) {
      updateFile(report, item.id, { status: "failed", error: toErrorText(e) });
    }
  };

  const addFiles = (report: ReportUploadKind, files: File[], date: string) => {
    const added = files
      .map((file): SnapshotUploadFile => {
        return { id: crypto.randomUUID(), file, status: "uploading", progress: 0, error: null, key: null, uploadedAt: null };
      });
    updateEntry(report, (entry) => {
      return { ...entry, files: [...entry.files, ...added] };
    });
    for (const item of added) {
      void uploadFile(report, item, date);
    }
  };

  const retryFile = (report: ReportUploadKind, id: string, date: string) => {
    const item = stateRef.current[report].files.find((f) => f.id === id);
    if (item) void uploadFile(report, item, date);
  };

  const markFileDeleted = (report: ReportUploadKind, key: string) => {
    updateEntry(report, (entry) => {
      const files = entry.files.filter((f) => f.key !== key);
      return { files };
    });
  };

  const resetForDate = () => {
    setState((current) => {
      const next = { ...current };
      for (const report of REPORTS) {
        const files = current[report].files.filter((f) => f.status !== "uploaded");
        next[report] = { files };
      }
      return next;
    });
  };

  return { state, isUploading, addFiles, retryFile, removeFile, markFileDeleted, resetForDate };
};
